import {
  BMComponentsType,
  BMDivider,
  BMPrimitives,
  BMText,
  useBMTheme,
} from "@traveloka/district-bloom";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useFormContext, useWatch } from "react-hook-form";
import { FlatList, StyleSheet } from "react-native";
import { VisibilitySensor } from "../../../../../components";
import BMView from "../../../../../components/View/BMView";
import { useMobileContentResource } from "../../../../../core/resource/MobileResourceProvider";
import { useTravelerPickerContextV2 } from "../../../../../providers/TravelerPickerContextV2";
import { formatMessage } from "../../../../../utils";
import NoResult from "../../NoResult/NoResult";
import CheckboxEmployee from "../CheckboxEmployee";
import { Employee } from "../types";
import { useCheckEmployeeDisabled } from "./hooks";
import { SelectEmployeeFormValues } from "./types";
import { filterEmployees, filterEmployeesByDivision } from "./utils";

const INITIAL_RENDER_COUNT = 30;
const ITEM_HEIGHT = 56;
const setValueConfig = {
  shouldDirty: true,
  shouldTouch: true,
};

export default function SelectEmployeeDrawerAllEmployee() {
  const {
    employeeList,
    AddEmployeeWidget = () => null,
    permissions: {
      travelerPicker: { canCreateEmployee },
    },
    visible,
  } = useTravelerPickerContextV2();
  const { CorporateTravelerPicker: cr } = useMobileContentResource();
  const { control, setValue } = useFormContext<SelectEmployeeFormValues>();
  const [selectedEmployees, divisions, searchEmployee] = useWatch({
    control,
    name: ["selectedEmployees", "divisions", "searchEmployee"],
  });
  const [numberToRender, setNumberToRender] = useState(INITIAL_RENDER_COUNT);

  const { BMComponents } = useBMTheme();
  const styles = createStyles(BMComponents);

  useEffect(() => {
    setNumberToRender(INITIAL_RENDER_COUNT);
  }, [divisions, searchEmployee, visible]);

  const handleNextPage = useCallback(() => {
    setNumberToRender((prev) => prev + INITIAL_RENDER_COUNT);
  }, []);

  const handleEmployeeChange = useCallback(
    (employee: Employee) => {
      const isSelected = selectedEmployees.find(
        (e) => e.email === employee.email
      );

      if (isSelected) {
        return selectedEmployees.filter((e) => e.email !== employee.email);
      }

      return [...selectedEmployees, employee];
    },
    [selectedEmployees]
  );

  const filteredEmployeeList = employeeList
    .filter(filterEmployees(searchEmployee))
    .filter(filterEmployeesByDivision(divisions));
  const shownEmployeeList = filteredEmployeeList.slice(0, numberToRender);

  const isDisabled = useCheckEmployeeDisabled();

  const sensorPosition = useMemo(
    () => shownEmployeeList.length - 10,
    [shownEmployeeList.length]
  );
  const renderItem = useCallback(
    ({ item, index }: { item: Employee; index: number }) => (
      <>
        {index === sensorPosition && (
          <VisibilitySensor triggerOnce onIntersect={handleNextPage}>
            <BMView />
          </VisibilitySensor>
        )}
        <CheckboxEmployee
          testID={`all-employee-checkbox-${index}`}
          style={styles.padding}
          disabled={isDisabled}
          employee={item}
          value={Boolean(selectedEmployees.find((e) => e.email === item.email))}
          onChange={() =>
            setValue(
              "selectedEmployees",
              handleEmployeeChange(item),
              setValueConfig
            )
          }
          searchKeyword={searchEmployee}
        />
      </>
    ),
    [
      isDisabled,
      selectedEmployees,
      searchEmployee,
      sensorPosition,
      styles.padding,
      handleEmployeeChange,
      handleNextPage,
      setValue,
    ]
  );
  return (
    <BMView style={styles.container} spacing="lg">
      <BMView row justify="between">
        <BMText variant="uiLargeLi" ink="BMStyleTextInkSecondary">
          {formatMessage(cr.allEmployeeSectionTitle, {
            num: filteredEmployeeList.length,
          })}
        </BMText>
        {canCreateEmployee && (
          <AddEmployeeWidget
            addSelectedEmployee={(item) =>
              setValue(
                "selectedEmployees",
                handleEmployeeChange(item),
                setValueConfig
              )
            }
          />
        )}
      </BMView>
      {shownEmployeeList.length ? (
        <BMView style={styles.innerWrapper}>
          <FlatList
            data={shownEmployeeList}
            initialNumToRender={10}
            windowSize={5}
            maxToRenderPerBatch={5}
            updateCellsBatchingPeriod={80}
            removeClippedSubviews
            keyExtractor={(item) => item.email}
            getItemLayout={getItemLayout}
            ItemSeparatorComponent={() => <BMDivider type="SUBTLE" />}
            renderItem={renderItem}
          />
        </BMView>
      ) : (
        <NoResult title={cr.noResultTitle} description={cr.noResultInfoText} />
      )}
    </BMView>
  );
}

function createStyles(BMComponents: BMComponentsType) {
  return StyleSheet.create({
    container: {
      marginTop: BMPrimitives.BMSize.xl,
      marginBottom: BMPrimitives.BMSize.xl2,
    },
    innerWrapper: {
      borderColor: BMComponents.BMStyleLineBordercolorSubtle,
      borderWidth: BMPrimitives.BMBorder.width.thick,
      borderRadius: BMComponents.BMStyleCardRadiusCompact,
    },
    padding: {
      padding: BMPrimitives.BMSize.md,
    },
  });
}

function getItemLayout(_: any, index: number) {
  return {
    length: ITEM_HEIGHT,
    offset: ITEM_HEIGHT * index,
    index,
  };
}
